import { useI18n } from '@/i18n';
import type { Locale } from 'date-fns';
import { format, formatDistance } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import type { Stripe } from 'stripe';
import { computed } from 'vue';

interface StripeInvoice {
  id: string;
  created: number;
  amount_due: number;
  currency: string;
}

interface StripeCoupon {
  amount_off: number | null;
  percent_off: number | null;
  currency: string | null;
  duration: string;
  duration_in_months: number | null;
}

interface StripeDiscount {
  end: number | null;
}

interface StripeSubscription {
  id: string;
  plan: { id: string; product: { id: string } };
  status: string;
}

interface Offering {
  plans: any;
}

function dateFnsLocaleForI18n(code: string): Locale {
  if (code.startsWith('es')) return es;
  return enUS;
}

const zeroDecimalCurrencies = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

export function useBillingUtils() {
  const { t, locale } = useI18n();
  const dateOptions = computed(() => ({ locale: dateFnsLocaleForI18n(locale.value) }));

  function formatPrice(total: number, currency: string = 'USD', dropZeros?: boolean): string {
    let minimumFractionDigits = 0;
    if (!zeroDecimalCurrencies.has(currency.toUpperCase())) total /= 100;
    if (!dropZeros || total % 1 !== 0) minimumFractionDigits = 2;
    return new Intl.NumberFormat(locale.value, {
      style: 'currency',
      currency,
      minimumFractionDigits,
    }).format(total);
  }

  function paymentString(invoice: StripeInvoice): string {
    const currency = invoice.currency.toUpperCase();
    const opts = {
      total: formatPrice(invoice.amount_due, currency) + ' ' + currency,
      date: format(new Date(invoice.created * 1000), 'PPP', dateOptions.value),
    };
    return t('billing_payment', opts);
  }

  /** Upcoming invoice preview: not a completed payment — use due date, not "Paid …". */
  function upcomingInvoicePaymentString(invoice: Stripe.Invoice): string {
    const currency = invoice.currency.toUpperCase();
    const total = formatPrice(invoice.amount_due, currency, true) + ' ' + currency;
    const ts =
      invoice.next_payment_attempt ?? invoice.period_end ?? invoice.lines?.data?.[0]?.period?.end ?? invoice.created;
    const date = format(new Date(ts * 1000), 'PPP', dateOptions.value);
    return t('billing_upcoming_payment', { total, date });
  }

  function couponAmount(coupon: StripeCoupon, off?: boolean): string {
    const total = coupon.amount_off
      ? formatPrice(coupon.amount_off, coupon.currency || undefined)
      : `${coupon.percent_off}%`;
    return off ? t('billing_amount_off', { total }) : total;
  }

  function couponDuration(coupon: StripeCoupon): string {
    return coupon.duration === 'repeating'
      ? t('billing_duration_repeating', { duration: coupon.duration_in_months })
      : t(`billing_duration_${coupon.duration}`);
  }

  /**
   * Natural sentence for the active-coupon alert (no bullet separators; avoids the word “Repeating”).
   */
  function couponDiscountBenefit(coupon: StripeCoupon): string {
    const pct = coupon.percent_off != null && coupon.percent_off > 0 ? Math.round(Number(coupon.percent_off)) : null;
    const amt =
      coupon.amount_off != null && coupon.amount_off > 0
        ? formatPrice(coupon.amount_off, coupon.currency || undefined, true)
        : null;

    if (pct != null) {
      if (coupon.duration === 'repeating') {
        const months = coupon.duration_in_months ?? 0;
        if (months === 1) {
          return t('billing_discount_percent_repeating_one_month', { percent: pct });
        }
        return t('billing_discount_percent_repeating', { percent: pct, months });
      }
      if (coupon.duration === 'once') {
        return t('billing_discount_percent_once', { percent: pct });
      }
      if (coupon.duration === 'forever') {
        return t('billing_discount_percent_forever', { percent: pct });
      }
    }

    if (amt != null) {
      if (coupon.duration === 'once') {
        return t('billing_discount_amount_once', { amount: amt });
      }
      if (coupon.duration === 'repeating') {
        const months = coupon.duration_in_months ?? 0;
        if (months === 1) {
          return t('billing_discount_amount_repeating_one_month', { amount: amt });
        }
        return t('billing_discount_amount_repeating', { amount: amt, months });
      }
      if (coupon.duration === 'forever') {
        return t('billing_discount_amount_forever', { amount: amt });
      }
    }

    return '';
  }

  function discountEnd(discount: StripeDiscount): string {
    return (discount.end && formatDistance(new Date(discount.end * 1000), new Date(), dateOptions.value)) || '';
  }

  function getCurrentPlan(subs: StripeSubscription[], planIds: string[]) {
    if (!planIds || !subs || subs.length === 0) return null;

    for (const sub of subs) {
      const productId = sub.plan?.product?.id;
      if (productId && planIds.includes(productId)) {
        return sub.plan;
      }
    }
    const firstActive = subs.find((sub) => sub.status === 'active' || sub.status === 'trialing');
    return firstActive ? firstActive.plan : null;
  }

  function getPlanIds(offerings: Offering | null): string[] {
    if (!offerings || !offerings.plans?.plans) return [];

    const ids: string[] = [];
    const plans = offerings.plans.plans;

    if (Array.isArray(plans)) {
      plans.forEach((product) => {
        Object.values(product).forEach((interval: any) => {
          if (typeof interval !== 'string') {
            Object.values(interval).forEach((currencyPlan: any) => {
              if (typeof currencyPlan === 'string') {
                ids.push(currencyPlan);
              }
            });
          }
        });
      });
    } else if (typeof plans === 'object') {
      Object.values(plans).forEach((product: any) => {
        Object.values(product).forEach((interval: any) => {
          if (interval && typeof interval !== 'string') {
            Object.values(interval).forEach((currencyPlan: any) => {
              if (typeof currencyPlan === 'string') {
                ids.push(currencyPlan);
              }
            });
          }
        });
      });
    }

    return [...new Set(ids)];
  }

  return {
    formatPrice,
    paymentString,
    upcomingInvoicePaymentString,
    couponAmount,
    couponDuration,
    couponDiscountBenefit,
    discountEnd,
    getCurrentPlan,
    getPlanIds,
  };
}
